import type { ReactNode } from 'react';
import { Icon } from '../../primitives/icon/index.ts';
import { LiveRegion } from '../../primitives/LiveRegion.tsx';
import { Text } from '../../primitives/Text.tsx';
import styles from './FeedbackSurface.module.css';

/**
 * Design system § 14 — the feedback surface.
 *
 * A miss is `near-miss` amber and NEVER `error`. Getting something wrong is
 * part of practice: error informs and invites another attempt, and coral is
 * reserved for a real system failure (§ 2, § 3.2, § 13).
 *
 * The component announces the outcome and its value in a polite live region,
 * which is § 12's requirement. Both the outcome and the value come from the
 * caller as text — this library does not know what a note is (research D-025).
 *
 * ONE COMPOSITION, FOR BOTH OUTCOMES.
 *
 * § 14's panel: a disc, a title and a line, inside the screen it belongs to.
 * A hit and a miss take the same panel in the same place, so the learner reads
 * the result rather than re-reading the screen — what changes between them is
 * the colour, the glyph and the sentence, which is the whole of what changed.
 *
 * Intensity still rises on success (§ 4 grad/success, § 9's rings expanding
 * from the acknowledged element), but it rises WITHIN the panel: the disc
 * glows and pulses, the surface takes the gradient. That is the difference
 * between raising intensity and moving the learner to a different screen.
 */

interface FeedbackSurfaceBaseProps {
  /** The headline. One line. */
  title: ReactNode;
  /**
   * Names the correct value in text. § 14 requires the panel to do this, so
   * the reader learns the answer rather than only the verdict.
   */
  detail?: ReactNode;
  /**
   * What a screen reader hears: the outcome AND its human-readable value
   * (§ 12). Given separately from `title`/`detail` because those may carry
   * markup, and an announcement must be a plain string.
   */
  announcement: string;
}

interface FeedbackSurfaceProps extends FeedbackSurfaceBaseProps {
  outcome: 'correct' | 'miss';
}

export function FeedbackSurface({ outcome, title, detail, announcement }: FeedbackSurfaceProps) {
  return (
    <>
      <div className={styles.surface} data-outcome={outcome} data-state={`resolved-${outcome}`}>
        <span className={styles.mark} aria-hidden="true">
          <Icon name={outcome === 'correct' ? 'check' : 'close'} size={24} />
        </span>

        <span className={styles.body}>
          <Text as="p" variant="heading">
            {title}
          </Text>
          {/* Encouragement is one line, never a paragraph, and never ironic. */}
          {detail ? (
            <Text as="p" variant="body">
              {detail}
            </Text>
          ) : null}
        </span>
      </div>

      <LiveRegion message={announcement} />
    </>
  );
}
